/**
 * The Hudu-style typeahead hanging off the header's search input — README
 * §7. Groups matches by type (Company · Person · Ticket · Tech · Live comm ·
 * Closed comm · Message · Close note), each item showing the server's own
 * snippet verbatim (task-19 rule 1 — a snippet must come from the real
 * match, never re-derived here), plus "See all N results in History →".
 *
 * Deliberately thin: no channel tile, no CLOSED/DISMISSED/LIVE chip, no
 * per-item network fetch. That richer rendering is the History page's
 * result card (task-19 resolution #4 — the typeahead's snippet is
 * single-line, the page's card is fuller; this does not try to be both).
 *
 * Routing an item is a single fact: every result that carries a `threadId`
 * points at `/transcript/:id` — `TranscriptPage` (Task 20) already decides
 * whether that lands on the read-only transcript or redirects to the live
 * pane, by reading the thread's *current* state itself. Duplicating that
 * decision here would risk a second, possibly-stale answer to the same
 * question, so this component asks it exactly once, in exactly one place.
 * An item with no `threadId` (a locator with nothing to open yet) falls
 * back to "See all", which is always honest since it never claims a
 * destination this component can't back up.
 */
import { useNavigate } from 'react-router-dom'
import { useStore } from '../../store'
import { SEARCH_GROUP_LABEL, visibleGroups } from './format'
import { DEFAULT_SEARCH_DEBOUNCE_MS, useSearch } from './useSearch'

export interface SearchTypeaheadProps {
  debounceMs?: number
}

export function SearchTypeahead({ debounceMs = DEFAULT_SEARCH_DEBOUNCE_MS }: SearchTypeaheadProps = {}) {
  const q = useStore((s) => s.q)
  const searchOpen = useStore((s) => s.searchOpen)
  const set = useStore((s) => s.set)
  const navigate = useNavigate()

  const trimmed = q.trim()
  const { result } = useSearch(trimmed, 'everything', debounceMs)

  if (!searchOpen || trimmed === '' || result === null) return null

  const groups = visibleGroups(result)

  function goToHistory(): void {
    set({ hFilter: 'everything', searchOpen: false })
    navigate('/history')
  }

  function selectItem(threadId: string | undefined): void {
    set({ searchOpen: false })
    if (threadId) {
      navigate(`/transcript/${threadId}`)
    } else {
      goToHistory()
    }
  }

  return (
    <div
      role="listbox"
      aria-label="Search results"
      data-testid="search-typeahead"
      className="absolute left-0 top-[calc(100%+4px)] z-40 max-h-[420px] w-[360px] overflow-y-auto rounded-[10px] border border-subtle bg-elevated shadow-menu"
    >
      {groups.length === 0 ? (
        <p className="px-3 py-3 text-[13px] text-lo">No matches.</p>
      ) : (
        groups.map((group) => (
          <div key={group.type} data-testid={`typeahead-group-${group.type}`}>
            <p className="px-3 pt-2 text-[11px] font-semibold uppercase tracking-wide text-lo">{SEARCH_GROUP_LABEL[group.type]}</p>
            {group.items.map((item) => (
              <button
                key={item.id}
                type="button"
                data-testid={`typeahead-item-${item.id}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => selectItem(item.threadId)}
                className="block w-full px-3 py-1.5 text-left hover:bg-card"
              >
                <span className="block truncate text-[13px] text-hi">{item.label}</span>
                <span className="block truncate text-[12px] text-mid">{item.snippet}</span>
              </button>
            ))}
          </div>
        ))
      )}
      <button
        type="button"
        data-testid="typeahead-see-all"
        onMouseDown={(e) => e.preventDefault()}
        onClick={goToHistory}
        className="block w-full border-t border-subtle px-3 py-2 text-left text-[13px] font-medium text-cc-blue-text hover:bg-card"
      >
        See all {result.total} results in History →
      </button>
    </div>
  )
}
